import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, ArrowRightLeft, Check, ChevronLeft, ChevronRight, Clock3, History, RefreshCw, Settings2 } from 'lucide-react'
import { ApiError } from '../../api/client'
import './data-updates.css'

export type AdminRequest = <T>(path: string, options?: RequestInit) => Promise<T>
type SyncSettings = {
  interval_seconds: number
  min_interval_seconds: number
  max_interval_seconds: number
  sync_enabled: boolean
  updated_at: string | null
  next_run_at: string | null
  running: boolean
}
type SourceAttempt = {
  provider: string
  label: string
  api: string
  endpoint: string
  status: 'success' | 'partial' | 'failed' | 'skipped'
  duration_ms: number
  record_count: number
  error_type: string | null
  http_status: number | null
  error_summary: string | null
  fallback: boolean
  attempt_count: number
  failed_count: number
}
type SyncRecord = {
  id: number
  dataset: 'catalog' | 'quotes' | 'overview' | 'heatmap'
  market: 'CN' | 'US'
  status: 'success' | 'partial' | 'failed' | 'skipped'
  started_at: string
  finished_at: string
  duration_ms: number
  record_count: number
  error_summary: string | null
  note: string | null
  source_attempts?: SourceAttempt[]
}
type SyncHistory = { items: SyncRecord[]; page: number; page_size: number; total: number }
const STATUS = { success: '成功', partial: '部分成功', failed: '失败', skipped: '跳过' }
const DATASET = { catalog: '证券目录', quotes: '股票行情', overview: '市场概览', heatmap: '市场热力图' }
const numbers = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 })
const dates = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false, timeZone: 'Asia/Shanghai',
})
function formatDate(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : dates.format(date)
}
function message(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) return '登录已过期，请重新登录。'
    if (error.code === 'INVALID_ARGUMENT') return error.message
    if (error.status === 0) return '暂时无法连接服务，请检查网络后重试。'
    if (error.status === 403) return '会话验证未通过，请重新登录后重试。'
    return '数据更新服务暂时不可用，请稍后重试。'
  }
  return '读取或保存失败，请稍后重试。'
}
function Notice({ text }: { text: string }) {
  return text ? <div className="admin-error" role="alert"><AlertCircle size={17} /><span>{text}</span></div> : null
}
function minutes(seconds: number) {
  return String(Math.round(seconds / 60 * 1_000_000) / 1_000_000)
}
function formatDuration(value: number) {
  return value < 1000 ? `${numbers.format(value)} 毫秒` : `${numbers.format(value / 1000)} 秒`
}
function SourceDetails({ attempts }: { attempts: SourceAttempt[] }) {
  if (!attempts.length) return null
  return <ol className="admin-sync-sources" aria-label="API 调用记录">{attempts.map((source, index) => <li key={`${source.provider}-${source.api}-${index}`}>
    <div className="admin-sync-source-heading"><strong>{source.label}</strong><span className={`admin-sync-result result-${source.status}`}>{STATUS[source.status]}</span>{source.fallback && <span className="admin-sync-source-tag">备用</span>}</div>
    <code className="admin-sync-source-api">{source.api}</code>
    <code className="admin-sync-source-endpoint">{source.endpoint}</code>
    <div className="admin-sync-source-metrics"><span>请求 {numbers.format(source.attempt_count)} 次</span><span>失败 {numbers.format(source.failed_count)} 次</span><span>{formatDuration(source.duration_ms)}</span><span>可用数据 {numbers.format(source.record_count)} 条</span></div>
    {(source.http_status !== null || source.error_type) && <div className="admin-sync-source-error-type">{source.http_status !== null && <span>HTTP {source.http_status}</span>}{source.error_type && <code>{source.error_type}</code>}</div>}
    {source.error_summary && <p className="admin-sync-error-text">{source.error_summary}</p>}
  </li>)}</ol>
}
function SourceSummary({ record, attempts }: { record: SyncRecord; attempts: SourceAttempt[] }) {
  const abnormal = attempts.filter((source) => source.status === 'failed' || source.status === 'partial' || source.failed_count > 0)
  const fallbacks = attempts.filter((source) => source.fallback)
  const availableFallback = fallbacks.some((source) => (source.status === 'success' || source.status === 'partial') && source.record_count > 0)
  const fallbackLabel = record.status === 'success' && availableFallback ? '已使用备用完成更新' : record.status === 'partial' && availableFallback ? '备用已补充部分数据' : '已尝试备用来源'
  return <>{abnormal.length > 0 && <div className="admin-sync-source-alert"><AlertCircle size={14} /><span>异常 API：{abnormal.map((source) => `${source.label} · ${source.api}`).join('；')}</span></div>}{fallbacks.length > 0 && <div className={`admin-sync-fallback ${availableFallback && (record.status === 'success' || record.status === 'partial') ? 'has-data' : ''}`}><ArrowRightLeft size={13} /><span>{fallbackLabel}</span></div>}</>
}

export function DataUpdatesPanel({ request }: { request: AdminRequest }) {
  // The parent request includes the current session. Keep it fresh without restarting effects on every render.
  const requestRef = useRef(request)
  useEffect(() => { requestRef.current = request }, [request])
  const mounted = useRef(false)
  const controllers = useRef(new Set<AbortController>())
  const settingsVersion = useRef(0)
  const historyVersion = useRef(0)
  const dirty = useRef(false)
  const [settings, setSettings] = useState<SyncSettings | null>(null)
  const [period, setPeriod] = useState('')
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [settingsError, setSettingsError] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [history, setHistory] = useState<SyncHistory | null>(null)
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [historyError, setHistoryError] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [market, setMarket] = useState('')
  const [dataset, setDataset] = useState('')

  useEffect(() => {
    mounted.current = true
    const pending = controllers.current
    return () => {
      mounted.current = false
      settingsVersion.current += 1
      historyVersion.current += 1
      pending.forEach((controller) => controller.abort())
      pending.clear()
    }
  }, [])
  const loadSettings = useCallback(async () => {
    const version = ++settingsVersion.current
    const controller = new AbortController()
    controllers.current.add(controller)
    setLoadingSettings(true)
    setSettingsError('')
    try {
      const result = await requestRef.current<SyncSettings>('/admin/sync/settings', { signal: controller.signal })
      if (!mounted.current || version !== settingsVersion.current) return
      setSettings(result)
      if (!dirty.current) setPeriod(minutes(result.interval_seconds))
    } catch (error) {
      if (mounted.current && version === settingsVersion.current && !controller.signal.aborted) setSettingsError(message(error))
    } finally {
      controllers.current.delete(controller)
      if (mounted.current && version === settingsVersion.current) setLoadingSettings(false)
    }
  }, [])
  const loadHistory = useCallback(async () => {
    const version = ++historyVersion.current
    const controller = new AbortController()
    controllers.current.add(controller)
    setLoadingHistory(true)
    setHistoryError('')
    const query = new URLSearchParams({ page: String(page), page_size: '20' })
    if (status) query.set('status', status)
    if (market) query.set('market', market)
    if (dataset) query.set('dataset', dataset)
    try {
      const result = await requestRef.current<SyncHistory>(`/admin/sync/history?${query}`, { signal: controller.signal })
      if (!mounted.current || version !== historyVersion.current) return
      setHistory(result)
    } catch (error) {
      if (mounted.current && version === historyVersion.current && !controller.signal.aborted) setHistoryError(message(error))
    } finally {
      controllers.current.delete(controller)
      if (mounted.current && version === historyVersion.current) setLoadingHistory(false)
    }
  }, [page, status, market, dataset])
  useEffect(() => { void loadSettings() }, [loadSettings])
  useEffect(() => { void loadHistory() }, [loadHistory])

  function editPeriod(value: string) {
    dirty.current = true
    setPeriod(value)
    setFeedback('')
    setSettingsError('')
  }
  async function savePeriod(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!settings || saving) return
    const interval = Number(period) * 60
    const seconds = Math.round(interval)
    if (!period.trim() || !Number.isFinite(interval) || Math.abs(seconds - interval) > .0001 || seconds < settings.min_interval_seconds || seconds > settings.max_interval_seconds) {
      setSettingsError('请输入 1–1440 分钟的更新周期，精确到整秒。')
      return
    }
    const version = ++settingsVersion.current
    const controller = new AbortController()
    controllers.current.add(controller)
    setSaving(true)
    setSettingsError('')
    setFeedback('')
    try {
      const result = await requestRef.current<SyncSettings>('/admin/sync/settings', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval_seconds: seconds }), signal: controller.signal,
      })
      if (!mounted.current || version !== settingsVersion.current) return
      setSettings(result)
      setPeriod(minutes(result.interval_seconds))
      dirty.current = false
      setFeedback(result.sync_enabled ? '已保存，后续任务按新周期安排，正在执行的更新不受影响。' : '已保存更新周期。自动更新当前未启用，启用后将使用此设置。')
    } catch (error) {
      if (mounted.current && version === settingsVersion.current && !controller.signal.aborted) setSettingsError(message(error))
    } finally {
      controllers.current.delete(controller)
      if (mounted.current && version === settingsVersion.current) setSaving(false)
    }
  }
  const totalPages = Math.max(1, Math.ceil((history?.total ?? 0) / 20))
  const hasFilters = Boolean(status || market || dataset)
  function changeFilter(setter: (value: string) => void, value: string) {
    setPage(1)
    setHistory(null)
    setter(value)
  }
  return <div className="admin-updates">
    <section className="admin-sync-settings" aria-labelledby="sync-settings-title" aria-busy={loadingSettings}>
      <div className="admin-list-heading"><div><h2 id="sync-settings-title"><Settings2 size={19} />更新周期</h2><p>股票行情、市场概览与热力图共用此更新周期。</p><p>证券目录单独更新：启用后每天刷新，失败后每分钟重试。</p></div><span className={`admin-sync-indicator ${settings?.running ? 'is-running' : ''}`}><i />{settings ? settings.running ? '正在更新' : settings.sync_enabled ? '自动更新已启用' : '自动更新未启用' : '读取设置中'}</span></div>
      <div className="admin-sync-setting-body">
        <form className="admin-sync-form" onSubmit={(event) => void savePeriod(event)}>
          <Notice text={settingsError} />
          <label className="admin-field" htmlFor="sync-period"><span id="sync-period-label" className="admin-field-label">每隔多久更新一次</span><div className="admin-sync-period-control"><input id="sync-period" aria-labelledby="sync-period-label" className="admin-input" type="number" inputMode="decimal" min="1" max="1440" step="any" value={period} placeholder="30" onChange={(event) => editPeriod(event.target.value)} disabled={!settings || saving} required aria-describedby="sync-period-help" /><span>分钟</span></div><span id="sync-period-help" className="admin-field-help">可设置 1 分钟至 24 小时，保存后无需重启服务。</span></label>
          <div className="admin-sync-presets" aria-label="常用更新周期">{[5, 15, 30, 60].map((value) => <button key={value} type="button" className={Number(period) === value ? 'is-selected' : ''} aria-pressed={Number(period) === value} disabled={!settings || saving} onClick={() => editPeriod(String(value))}>{value} 分钟</button>)}</div>
          <div className="admin-sync-save-row"><button className="admin-button admin-button-primary" type="submit" disabled={!settings || saving || loadingSettings}>{saving ? <RefreshCw className="admin-spin" size={16} /> : <Check size={16} />}{saving ? '保存中' : '保存周期'}</button>{!settings && !loadingSettings && <button className="admin-button" type="button" onClick={() => void loadSettings()}>重试加载设置</button>}</div>
          {feedback && <p className="admin-sync-feedback" role="status"><Check size={16} />{feedback}</p>}
        </form>
        <div className="admin-sync-schedule"><Clock3 size={23} /><dl><div><dt>当前周期</dt><dd>{settings ? `每 ${numbers.format(settings.interval_seconds / 60)} 分钟` : '—'}</dd></div><div><dt>下次更新 · 北京时间</dt><dd>{settings?.next_run_at ? formatDate(settings.next_run_at) : settings?.running ? '本轮结束后安排' : settings && !settings.sync_enabled ? '尚未启用' : '等待任务安排'}</dd></div><div><dt>周期最后修改</dt><dd>{settings?.updated_at ? formatDate(settings.updated_at) : '默认周期'}</dd></div></dl></div>
      </div>
      {settings && !settings.sync_enabled && <p className="admin-sync-disabled"><AlertCircle size={16} />服务未启用自动更新。周期设置会保留，启用自动更新后生效。</p>}
    </section>
    <section className="admin-key-section admin-sync-history" aria-labelledby="sync-history-title" aria-busy={loadingHistory}>
      <div className="admin-list-heading"><div><h2 id="sync-history-title"><History size={19} />更新历史</h2><p>查看每项任务的完成情况。所有时间以北京时间显示。</p></div><button className="admin-button" type="button" disabled={loadingHistory || loadingSettings || saving} onClick={() => { void loadSettings(); void loadHistory() }}><RefreshCw size={16} className={loadingHistory ? 'admin-spin' : ''} />{loadingHistory ? '加载中' : '刷新记录'}</button></div>
      <div className="admin-toolbar admin-sync-filters"><select className="admin-input" aria-label="筛选更新状态" value={status} onChange={(event) => changeFilter(setStatus, event.target.value)}><option value="">全部结果</option>{Object.entries(STATUS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><select className="admin-input" aria-label="筛选更新市场" value={market} onChange={(event) => changeFilter(setMarket, event.target.value)}><option value="">全部市场</option><option value="CN">A 股</option><option value="US">美股</option></select><select className="admin-input" aria-label="筛选更新数据" value={dataset} onChange={(event) => changeFilter(setDataset, event.target.value)}><option value="">全部数据</option>{Object.entries(DATASET).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></div>
      {historyError && <div className="admin-sync-history-error"><Notice text={historyError} /><button className="admin-button" type="button" disabled={loadingHistory} onClick={() => void loadHistory()}>重试加载记录</button></div>}
      <div className="admin-table-wrap"><table className="admin-table admin-sync-table"><thead><tr><th scope="col">开始时间</th><th scope="col">数据任务</th><th scope="col">结果</th><th scope="col">更新条数</th><th scope="col">耗时</th><th scope="col">详情</th></tr></thead><tbody>{history?.items.map((record) => {
        const attempts = record.source_attempts ?? []
        const hasReason = Boolean(record.note || attempts.some((source) => source.error_summary || source.error_type || (source.http_status !== null && source.http_status >= 400)))
        const detail = record.error_summary || (!hasReason ? record.status === 'failed' ? '本次更新失败，未返回具体原因。' : record.status === 'partial' ? '部分数据更新失败，未返回具体原因。' : '' : '')
        return <tr key={record.id}><td data-label="开始时间"><time dateTime={record.started_at}>{formatDate(record.started_at)}</time></td><td className="admin-sync-task" data-label="数据任务"><strong>{DATASET[record.dataset]}</strong><span>{record.market === 'CN' ? 'A 股' : '美股'}</span></td><td data-label="结果"><span className={`admin-sync-result result-${record.status}`}><i />{STATUS[record.status]}</span></td><td data-label="更新条数">{numbers.format(record.record_count)} 条</td><td data-label="耗时">{formatDuration(record.duration_ms)}</td><td className="admin-sync-detail" data-label="详情"><SourceSummary record={record} attempts={attempts} />{detail || record.note || attempts.length > 0 ? <details><summary>查看详情</summary><div>{detail && <p className={record.status === 'failed' || record.status === 'partial' ? 'admin-sync-error-text' : ''}>{detail}</p>}{record.note && <p>{record.note}</p>}<SourceDetails attempts={attempts} /><small>完成于 {formatDate(record.finished_at)}</small></div></details> : <span className="admin-sync-muted">—</span>}</td></tr>
      })}</tbody></table>{!history?.items.length && !historyError && <div className="admin-empty"><History size={28} /><h3>{loadingHistory ? '正在加载记录' : hasFilters ? '没有匹配的更新记录' : '暂无更新记录'}</h3><p>{loadingHistory ? '正在获取最近的数据更新结果。' : hasFilters ? '尝试其他结果、市场或数据类型。' : '启用此功能后的更新才会记录历史，下一轮任务完成后会显示在这里。'}</p>{hasFilters && !loadingHistory && <button className="admin-button" type="button" onClick={() => { setStatus(''); setMarket(''); setDataset(''); setPage(1); setHistory(null) }}>清空历史筛选</button>}</div>}</div>
      <div className="admin-list-footer admin-sync-pagination"><span>{history ? `共 ${numbers.format(history.total)} 条记录` : '更新记录'}{history && ` · 第 ${page} / ${totalPages} 页`}</span><div><button className="admin-button" type="button" aria-label="上一页更新记录" disabled={loadingHistory || page <= 1} onClick={() => { setHistory(null); setPage((value) => value - 1) }}><ChevronLeft size={16} />上一页</button><button className="admin-button" type="button" aria-label="下一页更新记录" disabled={loadingHistory || !history || page >= totalPages} onClick={() => { setHistory(null); setPage((value) => value + 1) }}>下一页<ChevronRight size={16} /></button></div></div>
    </section>
  </div>
}
